import { FormGroup, Modal } from 'react-bootstrap';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { post, put } from 'helpers/api';
import ErrorAlert from '../../project/components/new_listing_modal/error_alert';
import Button from 'shared/button';

function setInitialFormFields(editFields) {
  return editFields.reduce((accumulator, field) => {
    accumulator[field.name] = field.value || '';
    return accumulator;
  }, {});
}

function formCanSubmit(editFields, formFields) {
  return editFields.every(field => !field.isRequired || formFields[field.name].length > 0);
}

export default function SharedEditModal({
  databaseName,
  editFields,
  id,
  objectName,
  onCloseModal,
  onSuccessfulFormSubmit
}) {
  const [formFields, setFormFields] = useState(() => setInitialFormFields(editFields));
  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function formFieldChanged(fieldName, fieldValue) {
    setFormFields({ ...formFields, [fieldName]: fieldValue });
  }

  const canSubmitForm = formCanSubmit(editFields, formFields);

  async function submitForm(e) {
    e.preventDefault();
    if (!canSubmitForm) return;

    setIsSubmitting(true);
    setErrors([]);

    const { fetchMethod, url } = fetchParams();
    try {
      const res = await fetchMethod(url, { [databaseName]: formFields });
      setIsSubmitting(false);
      if (res.id) {
        onSuccessfulFormSubmit(res);
      } else {
        setErrors(res);
      }
    } catch {
      setIsSubmitting(false);
      const uploadType = id === '' ? 'created' : 'updated';
      setErrors([`${objectName} could not be ${uploadType}. Please try again or contact support for help.`]);
    }
  }

  function fetchParams() {
    if (id === '') return { fetchMethod: post, url: `/${databaseName}s` };

    return { fetchMethod: put, url: `/${databaseName}s/${id}` };
  }

  return (
    <Modal.Body className="p-5">
      <h2 className="text-center mt-0 mb-4">{id === '' ? 'Add a' : 'Edit'} {objectName}</h2>
      <ErrorAlert errorMessages={errors} hasErrors={errors.length > 0}/>
      <form onSubmit={submitForm}>
        {editFields.map((field, index) => (
          <FormGroup key={field.name}>
            <label htmlFor={`${objectName}-${field.name}`}>Add {objectName} {field.name}</label>
            <input
              autoFocus={index === 0}
              className="form-control"
              id={`${objectName}-${field.name}`}
              placeholder={`Enter ${objectName} ${field.name}`}
              value={formFields[field.name]}
              onChange={e => formFieldChanged(field.name, e.target.value)}
            />
          </FormGroup>
        ))}

        <div className="mt-5 gap-3 d-flex">
          <Button.Cancel
            disabled={isSubmitting}
            type="button"
            onClick={onCloseModal}
          >
            Cancel
          </Button.Cancel>
          <Button.Primary disabled={!canSubmitForm || isSubmitting} type="submit">
            {id === '' ? 'Add' : 'Update'} {objectName}
          </Button.Primary>
        </div>
      </form>
    </Modal.Body>
  );
}

SharedEditModal.propTypes = {
  databaseName: PropTypes.string.isRequired,
  editFields: PropTypes.arrayOf(PropTypes.shape({
    isRequired: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.string
  })).isRequired,
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  objectName: PropTypes.string.isRequired,
  title: PropTypes.string,
  onCloseModal: PropTypes.func.isRequired,
  onSuccessfulFormSubmit: PropTypes.func.isRequired
};
